<template>
  <!-- Вес за период: точки — замеры, линия — тенденция (взвешенное среднее
       за ±7 дней, utils/periodCharts.js), пунктир — ожидаемый путь при
       дефиците из профиля (модель Холла: спуск замедляется вместе с
       расходом). Тенденция считается от третьего замера. -->
  <div :class="{ 'weekly-weight': report }" :style="report ? {'--weekly-chart-font-size':labelSize} : null">
    <svg ref="chart" :viewBox="`0 0 ${W} ${H}`" class="w-full h-auto block" role="img" aria-label="Вес за период">
      <rect v-if="highlight" :x="highlight.x" :width="highlight.width" :y="T" :height="H - T - B" fill="var(--ink)" fill-opacity=".05" />
      <g v-for="g in grid" :key="g">
        <line :x1="L" :x2="W - R + 4" :y1="y(g)" :y2="y(g)" stroke="var(--ink)" stroke-opacity="0.08" />
        <text :x="W - R + 8" :y="y(g) + 4" class="lbl">{{ fmt(g) }}</text>
      </g>
      <polyline v-if="!report && expectedPts.length > 1" :points="expectedPts" fill="none" stroke="#6F9C9A" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" />
      <polyline v-if="report && trendPts.length > 1" :points="trendPts" fill="none" stroke="#6F9C9A" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" />
      <polyline v-if="report ? dots.length > 1 : trendPts.length > 1" :points="report ? dots.map(p => `${p.x},${p.y}`).join(' ') : trendPts" fill="none" stroke="#B98A5E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      <circle v-for="p in dots" :key="p.day" :cx="p.x" :cy="p.y" :r="dotR" fill="#FFFBF4" stroke="var(--ink)" stroke-opacity="0.6" stroke-width="1.3" />
      <circle v-if="last && !report" :cx="last.x" :cy="last.y" r="4" fill="#B98A5E" />
      <text v-if="last && !report" :x="last.x - 7" :y="last.y - 9" text-anchor="end" class="lbl strong">{{ fmt(last.kg) }}</text>
      <text v-for="t in ticks" :key="t.i" :x="t.x" :y="H - 4" :text-anchor="t.anchor" class="lbl">{{ t.label }}</text>
    </svg>
    <div class="legend">
      <span><svg v-if="report" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3" fill="#FFFBF4" stroke="var(--ink)" stroke-opacity=".6" stroke-width="1.3"/></svg><i v-else class="dot" />замер</span>
      <span v-if="report ? dots.length > 1 : trendPts.length > 1"><i class="ln" />{{ report ? 'измерения веса' : 'тенденция' }}</span>
      <span v-if="report && trendPts.length > 1"><svg width="28" height="8" viewBox="0 0 28 8" aria-hidden="true"><path d="M0 4h28" stroke="#6F9C9A" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round"/></svg>тенденция</span>
      <span v-if="!report && expectedPts.length > 1"><svg v-if="report" width="28" height="8" viewBox="0 0 28 8" aria-hidden="true"><path d="M0 4h28" stroke="#6F9C9A" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round"/></svg><i v-else class="exp" />ожидаемый путь при дефиците {{ deficitPct }}%</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useChartLabelSize } from '../utils/useChartLabelSize.js'
const {chart,labelSize} = useChartLabelSize(320)
import { daysBetween, addDays, longDate, tickIndexes, weightTrend, expectedPath } from '../utils/periodCharts.js'

const props = defineProps({
  points: { type: Array, required: true },   // [{ day: 'YYYY-MM-DD', kg }] — могут быть и до from
  from: { type: String, required: true },
  to: { type: String, required: true },
  report: Boolean,
  highlightFrom: String,
  highlightTo: String,
  deficitKcal: { type: Number, default: 0 },
  deficitPct: { type: Number, default: 0 },
})

// Высота 150 — как у столбиков; правое поле под подписи килограммов шрифтом 12, нижнее — под даты
const W = 320, H = 150, L = 6, R = 40, T = 10, B = 19
const chartFrom = computed(() => props.report ? (props.points.filter(p => p.day >= props.from && p.day <= props.to).map(p => p.day).sort()[0] || props.from) : props.from)
const n = computed(() => Math.max(1, daysBetween(chartFrom.value, props.to)))
const x = (i) => L + (W - L - R) * i / n.value
const visible = computed(() => props.points.filter(p => p.day >= props.from && p.day <= props.to))
const trend = computed(() => weightTrend(props.points, props.from, props.to))
const expected = computed(() => {
  if (props.report) return []
  if (!(props.report ? visible.value.length : trend.value.length) || !(props.deficitKcal > 0) || n.value < 20) return []
  const first = props.report ? visible.value[0] : trend.value[0]
  const start = daysBetween(chartFrom.value, first.day)
  return expectedPath(first.kg, props.deficitKcal, n.value - start).map((kg, k) => ({ i: start + k, kg }))
})

const range = computed(() => {
  const all = [...visible.value.map(p => Number(p.kg)), ...trend.value.map(p => p.kg), ...expected.value.map(p => p.kg)].filter(Number.isFinite)
  if (!all.length) return [0, 1]
  const lo = Math.min(...all), hi = Math.max(...all)
  const pad = Math.max(0.4, (hi - lo) * 0.15)
  return [lo - pad, hi + pad]
})
const y = (kg) => T + (H - T - B) * (1 - (kg - range.value[0]) / (range.value[1] - range.value[0]))
const grid = computed(() => {
  const [lo, hi] = range.value
  const step = hi - lo > 12 ? 2 : hi - lo > 5 ? 1 : 0.5
  const out = []
  for (let g = Math.ceil(lo / step) * step; g <= hi; g += step) out.push(Math.round(g * 10) / 10)
  return out.slice(0, 6)
})
const dotR = computed(() => n.value <= 14 ? 4.5 : n.value <= 35 ? 3.5 : n.value <= 120 ? 2.6 : 2)
const dots = computed(() => visible.value.map(p => ({ day: p.day, x: x(daysBetween(chartFrom.value, p.day)), y: y(Number(p.kg)) })))
const trendPts = computed(() => trend.value.map(p => `${x(daysBetween(chartFrom.value, p.day))},${y(p.kg)}`).join(' '))
const expectedPts = computed(() => expected.value.map(p => `${x(p.i)},${y(p.kg)}`).join(' '))
const last = computed(() => {
  const t = trend.value[trend.value.length - 1]
  return t ? { x: x(daysBetween(props.from, t.day)), y: y(t.kg), kg: t.kg } : null
})
// Круглые даты 1-е и 15-е, как под столбиками; край ряда — подпись прижата к краю
const ticks = computed(() => {
  const dates = Array.from({ length: n.value + 1 }, (_, i) => addDays(chartFrom.value, i))
  const indexes = props.report ? [0, ...tickIndexes(dates).filter(i => i >= 10)] : tickIndexes(dates)
  return indexes.map(i => {
    const cx = x(i)
    const anchor = cx < 42 ? 'start' : cx > W - R - 42 ? 'end' : 'middle'
    return { i, label: props.report ? `${Number(dates[i].slice(8))} ${['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'][Number(dates[i].slice(5,7))-1]}` : longDate(dates[i]), anchor, x: anchor === 'start' ? Math.max(L, cx) : anchor === 'end' ? Math.min(W - R + 4, cx) : cx }
  })
})
const highlight = computed(() => {
  if (!props.report || !props.highlightFrom || !props.highlightTo) return null
  const start = Math.max(0, daysBetween(chartFrom.value, props.highlightFrom))
  const end = Math.min(n.value, daysBetween(chartFrom.value, props.highlightTo))
  return end > start ? { x: x(start), width: x(end) - x(start) } : null
})
const fmt = (v) => Number(v).toFixed(1)
</script>

<style scoped>
.lbl { font-family: Inter, sans-serif; font-size: 12px; fill: var(--ink-70); }
.lbl.strong { font-size: 13px; font-weight: 700; fill: var(--ink); }
.legend { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 4px; font-family: Inter, sans-serif; font-size: var(--fs-11); color: var(--ink-70); }
.legend span { display: inline-flex; align-items: center; gap: 5px; }
.legend .dot { width: 8px; height: 8px; border-radius: 50%; border: 1.3px solid var(--ink-60); background: #FFFBF4; }
.legend .ln { width: 14px; border-top: 3px solid #B98A5E; border-radius: 2px; }
.legend .exp { width: 14px; border-top: 2px dashed #6F9C9A; }
.weekly-weight .lbl { font-size: var(--weekly-chart-font-size); }
.weekly-weight .legend { font-size: var(--fs-12); }
.weekly-weight .legend .ln { width: 28px; }

</style>
